import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchGraph } from '../../../services/graphService';
import { IntelligenceUnavailableError, fetchNetwork, listLeads } from '../intelligenceApi';
import type { ConnectionLead, Lead, NetworkGraph } from '../types';
import { fromLegacyGraph } from './networkModel';

const errorMessage = (e: any) => {
  const m = e?.response?.data?.message;
  return (Array.isArray(m) ? m.join(', ') : m) || e?.message || 'Could not load the network';
};

/**
 * Loads the enriched network (falls back to the older relationships graph when the
 * intelligence module is not deployed) and the pending connection leads.
 *
 * Ties that have ended come down with the rest and are filtered out of the
 * canvas in the client, rather than being fetched again when somebody asks
 * for them: the toggle is then instant, the time scrubber has its history to
 * hand, and a dossier can say what an entity used to be even while the canvas
 * shows only what is current.
 */
export function useNetworkData() {
  const [graph, setGraph] = useState<NetworkGraph | null>(null);
  const [limited, setLimited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leads, setLeads] = useState<ConnectionLead[]>([]);
  const [leadsAvailable, setLeadsAvailable] = useState(true);
  const request = useRef(0);

  const load = useCallback(
    async (quiet = false) => {
      const id = ++request.current;
      if (!quiet) setLoading(true);
      setError(null);
      try {
        let next: NetworkGraph;
        let isLimited = false;
        try {
          next = await fetchNetwork({ includePast: true });
        } catch (e) {
          if (!(e instanceof IntelligenceUnavailableError)) throw e;
          next = fromLegacyGraph(await fetchGraph({ currentOnly: false }));
          isLimited = true;
        }
        if (id !== request.current) return;
        setGraph(next);
        setLimited(isLimited);
        if (isLimited) setLeadsAvailable(false);
      } catch (e) {
        if (id === request.current) setError(errorMessage(e));
      } finally {
        if (id === request.current) setLoading(false);
      }
    },
    [],
  );

  const loadLeads = useCallback(async () => {
    try {
      const all: Lead[] = await listLeads({ kind: 'connection', status: 'pending' });
      setLeads((Array.isArray(all) ? all : []).filter((l): l is ConnectionLead => l.kind === 'connection'));
      setLeadsAvailable(true);
    } catch (e: any) {
      if (e?.response?.status === 404) setLeadsAvailable(false);
    }
  }, []);

  useEffect(() => {
    load(graph !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Leads only exist on servers with the intelligence module
  const loaded = graph !== null;
  useEffect(() => {
    if (loaded && !limited) loadLeads();
  }, [loaded, limited, loadLeads]);

  return { graph, limited, loading, error, reload: load, leads, setLeads, leadsAvailable, setLeadsAvailable, reloadLeads: loadLeads };
}
